const layer = (waveform, volumePercent, detuneCents = 0, pulseWidthPercent = 50) => ({
  enabled: volumePercent > 0,
  waveform,
  pulseWidthPercent,
  volumePercent,
  detuneCents,
})

export const SYNTH_INSTRUMENTS = Object.freeze({
  synth: preset('Synth', [layer('sine', 100), layer('sine', 0)], [0.002, 0.07, 0.8, 0.05]),
  piano: preset('Piano', [layer('triangle', 82), layer('sine', 32, 12)], [0.004, 0.32, 0.34, 0.45]),
  electric_piano: preset('Electric Piano', [layer('sine', 78), layer('triangle', 38, 7)], [0.008, 0.42, 0.48, 0.6]),
  organ: preset('Organ', [layer('square', 58, 0, 50), layer('sine', 52, 1200)], [0.015, 0.05, 0.92, 0.18]),
  guitar: preset('Acoustic Guitar', [layer('triangle', 78), layer('saw', 24, 7)], [0.003, 0.28, 0.28, 0.32]),
  electric_guitar: preset('Electric Guitar', [layer('saw', 62, -5), layer('square', 42, 7, 42)], [0.006, 0.18, 0.58, 0.42]),
  bass: preset('Bass', [layer('square', 62, 0, 38), layer('sine', 72, -12)], [0.004, 0.12, 0.64, 0.2]),
  strings: preset('Strings', [layer('saw', 52, -7), layer('saw', 52, 7)], [0.18, 0.35, 0.78, 0.75]),
  flute: preset('Flute', [layer('sine', 88), layer('triangle', 18, 12)], [0.06, 0.18, 0.82, 0.3]),
  clarinet: preset('Clarinet', [layer('square', 58, 0, 42), layer('sine', 36)], [0.035, 0.2, 0.76, 0.28]),
  brass: preset('Brass', [layer('saw', 62, -5), layer('square', 38, 5, 46)], [0.045, 0.16, 0.72, 0.34]),
  lead: preset('Lead', [layer('saw', 72, -9), layer('square', 48, 9, 35)], [0.008, 0.1, 0.68, 0.24]),
})

export const SYNTH_INSTRUMENT_OPTIONS = Object.freeze(Object.entries(SYNTH_INSTRUMENTS)
  .map(([value, settings]) => ({ value, label: settings.label })))

export function normalizeInstrument(value) {
  return Object.hasOwn(SYNTH_INSTRUMENTS, value) ? value : 'synth'
}

export function resolveInstrumentPreset(instrument) {
  return SYNTH_INSTRUMENTS[normalizeInstrument(instrument)]
}

export function applyInstrumentPreset(track, instrument) {
  const key = normalizeInstrument(instrument)
  const settings = resolveInstrumentPreset(key)
  return {
    ...track,
    instrument: key,
    adsr: { ...settings.adsr },
    layers: settings.layers.map(item => ({ ...item })),
    vibrato: { ...settings.vibrato },
  }
}

export function isTrackSoundModified(track) {
  const preset = resolveInstrumentPreset(track?.instrument)
  return !sameObject(track?.adsr, preset.adsr)
    || !sameObject(track?.vibrato, preset.vibrato)
    || track?.layers?.length !== preset.layers.length
    || preset.layers.some((item, index) => !sameObject(track.layers[index], item))
}

export function gmProgramToInstrument(program) {
  const value = Number(program)
  if (!Number.isInteger(value) || value < 0 || value > 127) return 'synth'
  if (value <= 7) return value <= 3 ? 'piano' : 'electric_piano'
  if (value <= 15) return 'electric_piano'
  if (value <= 23) return 'organ'
  if (value <= 31) return value >= 27 ? 'electric_guitar' : 'guitar'
  if (value <= 39) return 'bass'
  if (value <= 47) return 'strings'
  if (value <= 55) return 'strings'
  if (value <= 63) return 'brass'
  if (value <= 71) return value === 71 ? 'clarinet' : 'brass'
  if (value <= 79) return value >= 73 ? 'flute' : 'clarinet'
  if (value <= 87) return 'lead'
  return 'synth'
}

function preset(label, layers, [attack, decay, sustain, release]) {
  return Object.freeze({
    label,
    layers: Object.freeze(layers.map(item => Object.freeze(item))),
    adsr: Object.freeze({ attack, decay, sustain, release }),
    vibrato: Object.freeze({ enabled: false, rateHz: 0, depthHz: 0 }),
  })
}

function sameObject(actual, expected) {
  return Object.keys(expected).every(key => actual?.[key] === expected[key])
}
